import secureStorage from '../utils/secureStorage';
// StatisticsModal.js - Modal to show update statistics
// Shows "3/13 updated" with list of children

import React from 'react';

const StatisticsModal = ({ isOpen, onClose, statistics, type, className, onSelectChild }) => {
  if (!isOpen || !statistics) return null;

  const getTypeLabel = () => {
    switch (type) {
      case 'activity':
        return 'Aktiviti';
      case 'menu':
        return 'Menu';
      case 'daily_log':
        return 'Log Harian';
      default:
        return type;
    }
  };

  const getTypeColor = () => {
    switch (type) {
      case 'activity':
        return 'blue';
      case 'menu':
        return 'green';
      case 'daily_log':
        return 'purple';
      default:
        return 'purple';
    }
  };

  const color = getTypeColor();
  const { updated_children = [], not_updated_children = [], total_children = 0 } = statistics;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${color}-500 to-${color}-600 text-white px-6 py-4`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold">Status {getTypeLabel()}</h3>
              <p className="text-sm opacity-90">
                {updated_children.length}/{total_children} sudah dikemaskini
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {/* Updated Children */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-2">✅</span>
              <h4 className="text-lg font-semibold text-gray-800">
                Sudah Dikemaskini ({updated_children.length})
              </h4>
            </div>
            
            {updated_children.length === 0 ? (
              <p className="text-gray-500 italic">Tiada kanak-kanak yang dikemaskini lagi</p>
            ) : (
              <div className="space-y-2">
                {updated_children.map((child) => (
                  <div
                    key={child.id}
                    onClick={() => onSelectChild && onSelectChild(child)}
                    className={`flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg ${
                      onSelectChild ? 'cursor-pointer hover:bg-green-100' : ''
                    }`}
                  >
                    <span className="font-medium text-gray-800">{child.name}</span>
                    {child.updated_at && (
                      <span className="text-xs text-gray-500">
                        {new Date(child.updated_at).toLocaleTimeString('ms-MY', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Not Updated Children */}
          <div>
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-2">⚠️</span>
              <h4 className="text-lg font-semibold text-gray-800">
                Belum Dikemaskini ({not_updated_children.length})
              </h4>
            </div>
            
            {not_updated_children.length === 0 ? (
              <p className="text-green-600 font-medium">Semua kanak-kanak sudah dikemaskini!</p>
            ) : (
              <div className="space-y-2">
                {not_updated_children.map((child) => (
                  <div
                    key={child.id}
                    className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg"
                  >
                    <span className="font-medium text-gray-800">{child.name}</span>
                    <button
                      onClick={() => onSelectChild && onSelectChild(child)}
                      className="px-4 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
                    >
                      Kemaskini Sekarang
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Statistics Summary Button Component
export const StatisticsSummary = ({ className: classNameFilter, date, type, onOpenModal, refreshKey }) => {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = secureStorage.getItem('token');
        // Use classNameFilter prop first, then fallback to localStorage
        const classFilter = classNameFilter || localStorage.getItem('selectedClass');
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        if (!classFilter || classFilter === 'all') {
          setStats(null);
          setLoading(false);
          return;
        }

        const endpoint = type === 'activity' ? 'activities' :
                        type === 'menu' ? 'nutrition' :
                        'daily-logs';

        const response = await fetch(
          `${apiUrl}/api/${endpoint}/statistics?class_name=${encodeURIComponent(classFilter)}&date=${date}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [classNameFilter, date, type, refreshKey]);

  if (loading) return null;
  if (!stats || stats.total_children === 0) return null;

  const percentage = stats.total_children > 0
    ? Math.round((stats.updated_count / stats.total_children) * 100)
    : 0;

  return (
    <button
      onClick={() => onOpenModal(stats)}
      className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-50 transition cursor-pointer"
      data-testid="stats-summary-btn"
    >
      <span className="text-sm font-medium text-gray-700">
        <span className="text-purple-600 font-bold">{stats.updated_count}</span>
        <span className="text-gray-500">/{stats.total_children}</span>
      </span>
      <span className="text-xs text-gray-500">dikemaskini</span>
      <span className={`text-xs font-bold ${
        percentage >= 80 ? 'text-green-600' :
        percentage >= 50 ? 'text-orange-500' :
        'text-red-500'
      }`}>
        ({percentage}%)
      </span>
    </button>
  );
};

// P2: Per-Meal-Type Statistics for Nutrition
export const MealTypeStatistics = ({ date, onOpenModal, refreshKey, className }) => {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = secureStorage.getItem('token');
        // Use className prop first, then fallback to localStorage
        const classFilter = className || localStorage.getItem('selectedClass');
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        if (!classFilter || classFilter === 'all') {
          setStats(null);
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${apiUrl}/api/nutrition/statistics/by-meal-type?class_name=${encodeURIComponent(classFilter)}&date=${date}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching meal type statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [date, refreshKey, className]);

  if (loading) return null;
  if (!stats || stats.total_children === 0) return null;

  const mealConfig = [
    { key: "Sarapan", emoji: "🌅", color: "from-orange-400 to-amber-500", bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-600" },
    { key: "Snek Pagi", emoji: "🍪", color: "from-yellow-400 to-orange-400", bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-600" },
    { key: "Makan Tengah Hari", emoji: "🍱", color: "from-green-400 to-emerald-500", bg: "bg-green-50", border: "border-green-200", text: "text-green-600" },
    { key: "Snek Petang", emoji: "🧁", color: "from-pink-400 to-rose-500", bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-600" }
  ];

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="meal-type-statistics">
      {mealConfig.map(({ key, emoji, color, bg, border, text }) => {
        const mt = stats.meal_types?.[key];
        if (!mt) return null;
        const pct = stats.total_children > 0
          ? Math.round((mt.updated_count / stats.total_children) * 100) : 0;
        
        return (
          <button
            key={key}
            onClick={() => onOpenModal({
              total_children: stats.total_children,
              updated_count: mt.updated_count,
              not_updated_count: mt.not_updated_count,
              updated_children: mt.updated_children,
              not_updated_children: mt.not_updated_children
            }, key)}
            className={`${bg} ${border} border-2 rounded-xl p-3 hover:shadow-md transition-all text-left`}
            data-testid={`meal-stat-${key.replace(/\s/g, '-').toLowerCase()}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{emoji}</span>
              <span className="text-xs font-bold text-gray-700 truncate">{key}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-lg font-black ${text}`}>
                {mt.updated_count}/{stats.total_children}
              </span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                pct >= 80 ? 'bg-green-100 text-green-700' :
                pct >= 50 ? 'bg-orange-100 text-orange-700' :
                'bg-red-100 text-red-700'
              }`}>
                {pct}%
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default StatisticsModal;
